import type { PostType, TagComparator } from '../../graphqlEnums'
import { getPostTypeLabelPlural } from '~publish/helpers/postType'

import { formatDateRange } from '../../utils'

export interface BuildInsightsExportSummaryArgs {
  postCount?: number
  startDate: Date
  endDate: Date
  channelCount: number
  tags?: TagComparator
  postTypes?: readonly PostType[]
}

/**
 * Builds the post-download confirmation copy shown in the toast after an
 * Insights export completes. Anchored to the filter context at click time
 * because the user's mental model is "I clicked export with these filters,
 * here's what I got" — not what the worker happens to find when it runs.
 *
 * `postCount` is the totalCount from `useTopPosts`, which matches the rows
 * the worker will export for the same filter set.
 */
export function buildInsightsExportSummary({
  postCount,
  startDate,
  endDate,
  channelCount,
  tags,
  postTypes,
}: BuildInsightsExportSummaryArgs): string {
  const lead =
    postCount === undefined
      ? 'Downloaded posts'
      : `Downloaded ${postCount.toLocaleString()} ${
          postCount === 1 ? 'post' : 'posts'
        }`

  const range = formatDateRange(startDate, endDate)
  const channels = channelCount > 1 ? ` across ${channelCount} channels` : ''

  let tagsClause = ''
  if (tags?.isEmpty) {
    tagsClause = ', only posts without tags'
  } else if (tags?.in && tags.in.length > 0) {
    tagsClause = ', filtered by tags'
  }

  // Names the formats rather than saying "filtered by post type": the count is
  // already smaller than the customer expects, and the formats explain why.
  const labels = (postTypes ?? []).map(getPostTypeLabelPlural)
  const formats =
    labels.length > 2
      ? `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
      : labels.join(' and ')
  const postTypesClause = formats ? `, ${formats} only` : ''

  return `${lead} from ${range}${channels}${postTypesClause}${tagsClause}.`
}
